import {inject, Injectable} from '../di';
import {Logger} from '../logger';
import {AppConfig} from './app-config';
import {ConfigService} from './config-service';
import {AppInitializeService} from '../app-initialize-service';
import {promiseTimeout} from '../utils/promises';
import {getText} from '../utils/get-text';

const CONFIG_FILE_URL = `tv/config.js`;

/**
 * Service for loading main config file.
 * NOTE: we need separate service to avoid circular dependencies.
 */
@Injectable()
export class ConfigLoaderService implements AppInitializeService {
  private readonly logger = inject(Logger);
  private readonly configService = inject(ConfigService);

  public async init(): Promise<boolean> {
    const config = await this.getMainConfig();
    if (config) {
      try {
        if (config.region) {
          this.logger.warn(
            `[ConfigLoaderService] "region" in config.js is deprecated and has no effect. ` +
              `Set region via window.HOTEL_REGION in hotel.js instead.`,
          );
        }

        this.logger.debug(`[ConfigLoaderService] Applying main configuration...`);
        this.configService.applyConfig(config);
        // this.logger.debug(`[ConfigLoaderService] Configuration: `, this.configService.config);

        if (window.CONFIG) {
          window.CONFIG.test = () => this.testConfig(); // todo legacy
        }

        return true;
      } catch (err) {
        this.logger.critical(
          `[ConfigLoaderService] Unable to apply main configuration on top of the default configuration`,
          err,
        );
      }
    } else {
      this.logger.debug(`[ConfigLoaderService] Default config will be used instead`);
    }
    return false;
  }

  /**
   * Download and parse main configuration file.
   * In case of error it logs and returns null.
   */
  private async getMainConfig(): Promise<AppConfig | null> {
    // 1. Fetch the config file
    const maxRetries = 3;
    const retryDelay = 1000;

    let success = false;
    let configText: string | undefined = undefined;
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        configText = await getText(CONFIG_FILE_URL + `?c=${Date.now()}`);
        this.logger.debug(`[ConfigLoaderService] Main configuration file ${CONFIG_FILE_URL} loaded successfully`);
        success = true;
        break;
      } catch (error) {
        this.logger.warn(
          `[ConfigLoaderService] Failed to download main configuration file from ${CONFIG_FILE_URL} (attempt ${attempt}/${maxRetries})`,
          error,
        );

        if (attempt < maxRetries) {
          this.logger.debug(`[ConfigLoaderService] Retrying configuration file downloading in ${retryDelay}ms...`);
          await promiseTimeout(retryDelay);
        }
      }
    }

    if (!success || !configText) {
      this.logger.critical(
        `[ConfigLoaderService] Failed to download main configuration file from ${CONFIG_FILE_URL} after ${maxRetries} attempts`,
      );
      return null;
    }

    // 2. Executing config js code
    try {
      // Add source file mapping to display line/col numbers in case of exception
      configText = configText + `\n//# sourceURL=tv/config.js`;

      // Execute config.js code on global scope (window)
      eval.call(window, configText);
    } catch (err) {
      this.logger.critical(
        `[ConfigLoaderService] CRITICAL ERROR! Failed to parse main configuration file ${CONFIG_FILE_URL}`,
        err,
      );
      return null;
    }

    // 3. Parse the config object
    let parsedConfig: AppConfig;
    try {
      if (!window.config || typeof window.config !== 'object') {
        this.logger.critical(
          `[ConfigLoaderService] Configuration file is not valid! It should declare 'config' object like 'var config = {...}'`,
          window.config,
        );
        return null;
      }

      parsedConfig = {
        // NOTE: in legacy config format there could be variables defined on root scope. We still support old format,
        // but do not support root scope config variables. So we need to copy all of them to config object.
        // TODO: After CI/CD automation we will be able to change config format and get rid of global scope pollution
        //  in config.
        tv_daemon_url: window.tv_daemon_url as AppConfig['tv_daemon_url'],
        tv_channellist_type: window.tv_channellist_type as AppConfig['tv_channellist_type'],
        tv_auth_function: window.tv_auth_function as AppConfig['tv_auth_function'],
        tv_key_source: window.tv_key_source as AppConfig['tv_key_source'],

        ...(window.config as AppConfig),
      };
      window.config = undefined;
    } catch (e) {
      this.logger.critical(`[ConfigLoaderService] Unable to parse configuration file`, e);
      return null;
    }

    // TODO validate config for critical entries

    return parsedConfig;
  }

  private testConfig() {
    // TODO refactor
    //  Here we need to find differences between default config and current config
    //  Maybe it's better to use ZOD framework here to find missing mandatory fields and validates values (e.g. urls).

    // TODO move to config service to validate resulting configuration.
    //  Logging should be moved somewhere else, to ConfigValidatorService or something
    //  It is used for 1165 service code only

    const defaultConfig = window.CONFIG.config_default as Record<string, any>;
    const config = window.CONFIG.config_loaded as Record<string, any> | undefined;
    if (!config) {
      this.logger.warn(`[CONFIG] Config was not loaded!`);
      return false;
    }

    let tmp_diff = [];
    let tmp = this.list_diff(Object.keys(defaultConfig), Object.keys(config));
    for (const i in defaultConfig) {
      if (typeof defaultConfig[i] === 'object' && typeof config[i] === 'object') {
        tmp_diff = this.list_diff(Object.keys(defaultConfig[i]), Object.keys(config[i]));
        tmp_diff = tmp_diff.map(function (q) {
          return i + ':' + q;
        });
        tmp = tmp.concat(tmp_diff);
      }
      for (const o in defaultConfig[i]) {
        if (typeof defaultConfig[i][o] === 'object' && typeof config[i][o] === 'object') {
          tmp_diff = this.list_diff(Object.keys(defaultConfig[i][o]), Object.keys(config[i][o]));
          tmp_diff = tmp_diff.map(function (q) {
            return i + ':' + o + ':' + q;
          });
          tmp = tmp.concat(tmp_diff);
        }
      }
    }

    if (tmp.length) {
      this.logger.warn(`[CONFIG] Config lacks parameters: ${tmp.join(', ')}`);
      return false;
    } else {
      this.logger.warn(`[CONFIG] Config is OK`);
      return true;
    }
  }

  private list_diff<T = unknown>(a: Array<T>, b: Array<T>): Array<T> {
    return a.filter((item) => b.indexOf(item) === -1);
  }
}
